import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrashAlt,
  faReply,
  faUserLock,
  faEllipsisVertical,
  faShareNodes,
} from "@fortawesome/pro-regular-svg-icons";
import { Avatar, FormattedDate, DropDownMenu } from "../common";
import type { DropDownMenuItem } from "../common/DropDownMenu";
import { UserBasicDetailsDto } from "../../types/dtos/generic";
import React from "react";
import UserContext from "../../state/UserContext";
import { AnswerSetCommentVisibility } from "../../types/collab-docs/AnswerSetCommentVisibility";

function PrivateBadge({
  marginClassName = "mr-2",
  isClickable = false,
  onClick,
}: {
  marginClassName?: string;
  isClickable?: boolean;
  onClick?: () => void;
}) {
  const content = (
    <>
      <FontAwesomeIcon icon={faUserLock} size="xs" className="text-amber-600" />
      <span className="text-amber-600">Private</span>
    </>
  );

  if (isClickable && onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`inline-flex items-center gap-1 hover:underline cursor-pointer transition-opacity hover:opacity-70 ${marginClassName}`}
        title="Click to make this shared"
      >
        {content}
      </button>
    );
  }

  return (
    <span className={`inline-flex items-center gap-1 ${marginClassName}`}>
      {content}
    </span>
  );
}

function SharedBadge({
  marginClassName = "mr-2",
  isClickable = false,
  onClick,
}: {
  marginClassName?: string;
  isClickable?: boolean;
  onClick?: () => void;
}) {
  const content = (
    <>
      <FontAwesomeIcon
        icon={faShareNodes}
        size="xs"
        className="text-blue-600"
      />
      <span className="text-blue-600">Shared</span>
    </>
  );

  if (isClickable && onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`inline-flex items-center gap-1 hover:underline cursor-pointer transition-opacity hover:opacity-70 ${marginClassName}`}
        title="Click to make this private"
      >
        {content}
      </button>
    );
  }

  return (
    <span className={`inline-flex items-center gap-1 ${marginClassName}`}>
      {content}
    </span>
  );
}

interface IndividualCommentProps {
  commentId: string | number;
  comment: string;
  commentTimestamp: Date;
  author: UserBasicDetailsDto | undefined;
  allowDelete: boolean;
  disableDeleteButton: boolean;
  allowReply: boolean;
  onDeleteClick(commentId: string | number): void;
  onTargetCommentForReply(commentId: string | number): void;
  /** Replies, if there are any */
  children?: React.ReactNode | null;
  /** Optional visibility for collaborative document comments (tasks don't have visibility) */
  visibility?: AnswerSetCommentVisibility;
  allowVisibilityChange?: boolean;
  onVisibilityChange?(
    commentId: string | number,
    visibility: AnswerSetCommentVisibility
  ): void;
  /** Whether private notes should be shown (global toggle) */
  showPrivateNotes: boolean;
}

function IndividualComment({
  commentId,
  comment,
  commentTimestamp,
  author,
  visibility,
  allowVisibilityChange = false,
  onVisibilityChange = undefined,
  allowDelete,
  disableDeleteButton,
  allowReply,
  onDeleteClick,
  onTargetCommentForReply,
  children = null,
  showPrivateNotes,
}: IndividualCommentProps) {
  const userContext = React.useContext(UserContext);
  const isCommentFromLoggedInUser = userContext.user.id === author?.userId;
  const [isIndividuallyRevealed, setIsIndividuallyRevealed] =
    React.useState<boolean>(false);
  const changeToSharedVisibilityText = "Make this shared";
  const changeToPrivateVisibilityText = "Make this private";

  // Reset individual reveal state when global toggle is turned on
  React.useEffect(() => {
    if (showPrivateNotes) {
      setIsIndividuallyRevealed(false);
    }
  }, [showPrivateNotes]);

  // Determine if this private comment should be blurred
  const isPrivate = visibility === "PRIVATE";
  const shouldBlur = isPrivate && !showPrivateNotes && !isIndividuallyRevealed;

  const handleRevealClick = () => {
    if (shouldBlur) {
      setIsIndividuallyRevealed(true);
    }
  };

  // Build dropdown menu items - Reply comes before Delete when both are available
  const menuItems: DropDownMenuItem[] = [];
  if (allowReply) {
    menuItems.push({
      text: "Pages.CollaborativeDocument.Common.Reply",
      customArg: commentId,
      customIcon: <FontAwesomeIcon size="sm" icon={faReply} />,
    });
  }
  if (allowVisibilityChange && author?.userId === userContext.user.id) {
    menuItems.push({
      text:
        visibility === "PRIVATE"
          ? changeToSharedVisibilityText
          : changeToPrivateVisibilityText,
      customArg: commentId,
      customIcon:
        visibility === "PRIVATE" ? (
          <FontAwesomeIcon size="sm" icon={faShareNodes} />
        ) : (
          <FontAwesomeIcon size="sm" icon={faUserLock} />
        ),
    });
  }
  if (allowDelete) {
    menuItems.push({
      text: "Common.Delete",
      customArg: commentId,
      customIcon: <FontAwesomeIcon size="sm" icon={faTrashAlt} />,
      disabled: disableDeleteButton,
    });
  }

  const handleMenuItemClick = (itemIndex: number, customArg: any) => {
    const item = menuItems[itemIndex];
    if (item.disabled) {
      return;
    }
    if (item.text === "Pages.CollaborativeDocument.Common.Reply") {
      onTargetCommentForReply(customArg);
    } else if (item.text === "Common.Delete") {
      onDeleteClick(customArg);
    } else if (
      item.text === changeToSharedVisibilityText ||
      item.text === changeToPrivateVisibilityText
    ) {
      if (onVisibilityChange) {
        onVisibilityChange(
          customArg,
          item.text === changeToSharedVisibilityText ? "SHARED" : "PRIVATE"
        );
      } else {
        console.error(
          "You are not allowed to change the visibility of this comment"
        );
      }
    }
  };

  return (
    <div className="my-3" key={`comment_${commentId}`}>
      {isCommentFromLoggedInUser && (
        <div className="flex flex-row">
          <div className="flex-initial">
            {author.fullName && author.initials && (
              <div className="inline-block mr-2 pt-1">
                <Avatar
                  imageUrl={author.profileImageUrl}
                  userFullName={author.fullName}
                  userInitials={author.initials}
                  size={8}
                  initialsTextClassName="text-xs"
                />
              </div>
            )}
          </div>
          <div className="flex-grow relative group">
            <div
              style={{ whiteSpace: "pre-wrap" }}
              className={`user-task-comment-owner rounded-md text-sm p-2 ${
                menuItems.length > 0 ? "pr-6" : ""
              } flex-grow whitespace-normal break-words min-w-[1%] ${
                shouldBlur ? "cursor-pointer select-none" : ""
              }`}
              onClick={handleRevealClick}
            >
              {shouldBlur ? (
                <div className="relative">
                  <div className="blur-sm">{comment}</div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-200">
                      <FontAwesomeIcon icon={faUserLock} size="sm" />
                      Click to reveal private note
                    </span>
                  </div>
                </div>
              ) : (
                comment
              )}
            </div>
            {menuItems.length > 0 && (
              <div className="absolute top-1 right-2 print:hidden opacity-[0.25] group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
                <DropDownMenu
                  items={menuItems}
                  eventType="EVENT"
                  onItemClick={handleMenuItemClick}
                  showMenuEvenIfOnlyOneItem={true}
                  customButtonContents={
                    <FontAwesomeIcon
                      icon={faEllipsisVertical}
                      size="sm"
                      className="text-gray-500"
                    />
                  }
                  menuButtonClassName="p-1 focus:outline-none"
                  outerDivClassName="relative inline-block text-left"
                />
              </div>
            )}
            <div className="text-xs pt-0.5 text-gray-400">
              <div className="flex flex-row mt-[0px] items-center gap-1.5">
                {visibility && visibility === "PRIVATE" && (
                  <>
                    <PrivateBadge
                      marginClassName="mr-0"
                      isClickable={
                        allowVisibilityChange &&
                        author?.userId === userContext.user.id
                      }
                      onClick={() =>
                        onVisibilityChange &&
                        onVisibilityChange(commentId, "SHARED")
                      }
                    />
                    <span className="text-gray-300">|</span>
                  </>
                )}
                {visibility && visibility === "SHARED" && (
                  <>
                    <SharedBadge
                      marginClassName="mr-0"
                      isClickable={
                        allowVisibilityChange &&
                        author?.userId === userContext.user.id
                      }
                      onClick={() =>
                        onVisibilityChange &&
                        onVisibilityChange(commentId, "PRIVATE")
                      }
                    />
                    <span className="text-gray-300">|</span>
                  </>
                )}
                <FormattedDate
                  date={commentTimestamp}
                  displayMode="FROM-NOW"
                  convertFromUtc
                />
              </div>
            </div>
          </div>
        </div>
      )}
      {!isCommentFromLoggedInUser && (
        <div className="flex flex-row">
          <div className="flex-grow relative group">
            <div
              className={`user-task-comment-owner rounded-md text-sm p-2 flex-grow whitespace-normal break-words min-w-[1%] ${
                shouldBlur ? "cursor-pointer select-none" : ""
              }`}
              onClick={handleRevealClick}
            >
              {shouldBlur ? (
                <div className="relative">
                  <div className="blur-sm">{comment}</div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-200">
                      <FontAwesomeIcon icon={faUserLock} size="sm" />
                      Click to reveal private note
                    </span>
                  </div>
                </div>
              ) : (
                comment
              )}
            </div>
            {menuItems.length > 0 && (
              <div className="absolute top-1 right-2 print:hidden opacity-[0.25] group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
                <DropDownMenu
                  items={menuItems}
                  eventType="EVENT"
                  onItemClick={handleMenuItemClick}
                  showMenuEvenIfOnlyOneItem={true}
                  customButtonContents={
                    <FontAwesomeIcon
                      icon={faEllipsisVertical}
                      size="sm"
                      className="text-gray-500"
                    />
                  }
                  menuButtonClassName="p-1 rounded focus:outline-none"
                  outerDivClassName="relative inline-block text-left"
                />
              </div>
            )}
            <div className="text-xs pt-0.5 text-gray-400">
              <div className="flex flex-row justify-end mt-[0px] items-center">
                <FormattedDate
                  date={commentTimestamp}
                  displayMode="FROM-NOW"
                  convertFromUtc
                />
              </div>
            </div>
          </div>
          <div className="flex-initial">
            {author && author.fullName && author.initials && (
              <div className="inline-block ml-2 pt-1">
                <Avatar
                  imageUrl={author.profileImageUrl}
                  userFullName={author.fullName}
                  userInitials={author.initials}
                  size={8}
                  initialsTextClassName="text-xs"
                />
              </div>
            )}
          </div>
        </div>
      )}

      {children}

      {/* <div className="flex flex-row">
        {author && (
          <div className="inline-block mr-2 pt-1">
            <Avatar
              imageUrl={author.profileImageUrl}
              userFullName={author.fullName}
              userInitials={author.initials}
              size={8}
              initialsTextClassName="text-xs"
            />
          </div>
        )}
        <div className="rounded-md inline-block text-sm text-gray-500 p-2 bg-white shadow-md flex-grow whitespace-normal break-words min-w-[1%]">
          {comment}
        </div>
      </div>
      <div className="flex flex-row">
        <div className="basis-2/3 text-gray-400">
          <small className="pl-2">
            <FormattedDate
              date={commentTimestamp}
              displayMode="FROM-NOW"
              convertFromUtc
            />
          </small>
        </div>
        {(allowReply || allowDelete) && (
          <div className="basis-1/3 text-right print:hidden">
            {allowReply && (
              <button
                className="text-xs text-gray-400"
                onClick={() => onTargetCommentForReply(commentId)}
              >
                {t("Pages.CollaborativeDocument.Common.Reply")}
              </button>
            )}

            {allowDelete && (
              <button
                title={t("Common.Delete")}
                className="ml-2 disabled:text-gray-400 disabled:cursor-not-allowed"
                disabled={disableDeleteButton}
                onClick={() => onDeleteClick(commentId)}
              >
                <FontAwesomeIcon size="xs" icon={faTrashAlt} />
              </button>
            )}
          </div>
        )}
      </div>

      {children} */}
    </div>
  );
}

export default IndividualComment;
